import React from 'react';
import {
  Compass,
  Layers,
  Target,
  Eye,
  Grid,
  BookOpen,
  CheckCircle,
  XCircle
} from 'lucide-react';
import { ProductDesignPageHeader, ProductDesignSection } from './priorities-ai-design/ProductDesignDocPrimitives';

export const BestPracticesDesignPrinciples: React.FC = () => {
  return (
    <div>
      <ProductDesignPageHeader
        title="Best Practices & Design Principles"
        eyebrow="Principles · quality"
        backTo="/design-language"
        backLabel="Design language"
        subtitle="The foundational principles that guide every design decision, ensuring our visual identity reflects Peak Priorities' unique differentiators and creates lasting impact."
      />

      {/* Golden Ratio & Mathematical Harmony */}
      <ProductDesignSection title="Golden Ratio & Mathematical Harmony">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          The golden ratio (φ = 1.618) creates natural, pleasing proportions that feel inherently balanced. We use it as a foundation for spacing, sizing, and layout decisions.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)] mb-3">Golden Ratio Applications</h3>
            <ul className="list-disc list-inside text-[var(--text-sec)] space-y-2">
              <li><strong>Typography Scale:</strong> Base font size × 1.618 for headings</li>
              <li><strong>Spacing System:</strong> 8px base unit with golden ratio multipliers</li>
              <li><strong>Layout Proportions:</strong> Content width to sidebar ratios</li>
              <li><strong>Component Sizing:</strong> Button heights, card dimensions</li>
              <li><strong>Image Cropping:</strong> Aspect ratios for photography</li>
            </ul>
          </div>

          <div className="bg-[var(--surface-raised)] p-6">
            <h4 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-3">Golden Ratio Calculator</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span>Base Size:</span>
                <span className="font-mono">16px</span>
              </div>
              <div className="flex justify-between">
                <span>× 1.618:</span>
                <span className="font-mono">25.9px</span>
              </div>
              <div className="flex justify-between">
                <span>× 2.618:</span>
                <span className="font-mono">41.9px</span>
              </div>
              <div className="flex justify-between">
                <span>× 4.236:</span>
                <span className="font-mono">67.8px</span>
              </div>
            </div>
            <p className="text-xs text-[var(--text-sec)] mt-3">
              Use these proportions for consistent, harmonious sizing across all elements.
            </p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Peak Priorities Core Differentiators */}
      <ProductDesignSection title="Peak Priorities Core Differentiators">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          These unique differentiators must be reflected in every visual decision, creating a brand that's instantly recognizable and deeply meaningful.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Whole-Person Focus */}
          <div className="p-6 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <div className="flex items-center gap-3 mb-3">
              <Compass className="h-6 w-6 text-[var(--text-ter)]" />
              <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Whole-Person Focus</h3>
            </div>
            <p className="text-[var(--text-sec)] text-sm mb-3">
              Serving the person at work and in life—not just professional development in isolation.
            </p>
            <div className="space-y-2">
              <h4 className="font-heading font-semibold text-[var(--text-pri)]">Visual Implications:</h4>
              <ul className="list-disc list-inside text-[var(--text-sec)] text-sm space-y-1">
                <li>Dual-aspect metaphors (inner/outer terrain)</li>
                <li>Personal compass vs. organizational maps</li>
                <li>Integrated life/work visual narratives</li>
              </ul>
            </div>
          </div>

          {/* Systemic Layers */}
          <div className="p-6 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <div className="flex items-center gap-3 mb-3">
              <Layers className="h-6 w-6 text-sage-600" />
              <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">Systemic Layers of Impact</h3>
            </div>
            <p className="text-[var(--text-sec)] text-sm mb-3">
              Individual → Team → Organization: nested systems and scaling interventions.
            </p>
            <div className="space-y-2">
              <h4 className="font-heading font-semibold text-[var(--text-pri)]">Visual Implications:</h4>
              <ul className="list-disc list-inside text-[var(--text-sec)] text-sm space-y-1">
                <li>Zoomable, fractal structures</li>
                <li>Topographic maps with nested systems</li>
                <li>"Multi-summit expeditions" metaphors</li>
              </ul>
            </div>
          </div>

          {/* The 7 C's */}
          <div className="p-6 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <div className="flex items-center gap-3 mb-3">
              <Target className="h-6 w-6 text-[var(--mustard)]" />
              <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">The 7 C's Framework</h3>
            </div>
            <p className="text-[var(--text-sec)] text-sm mb-3">
              Clarity, Context, Coherence, Congruence, Conviction, Capacity, Catalysis—an integrated diagnostic and navigational framework.
            </p>
            <div className="space-y-2">
              <h4 className="font-heading font-semibold text-[var(--text-pri)]">Visual Implications:</h4>
              <ul className="list-disc list-inside text-[var(--text-sec)] text-sm space-y-1">
                <li>Radial compass or heptagonal system</li>
                <li>Star chart or wayfinding wheel</li>
                <li>Each "C" as a directional guide</li>
              </ul>
            </div>
          </div>

          {/* DEGAP Framework */}
          <div className="p-6 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <div className="flex items-center gap-3 mb-3">
              <Eye className="h-6 w-6 text-[var(--text-ter)]" />
              <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)]">DEGAP Framework</h3>
            </div>
            <p className="text-[var(--text-sec)] text-sm mb-3">
              Decide → Engage → Gather → Arrange → Prioritize: a concrete process that works at all levels.
            </p>
            <div className="space-y-2">
              <h4 className="font-heading font-semibold text-[var(--text-pri)]">Visual Implications:</h4>
              <ul className="list-disc list-inside text-[var(--text-sec)] text-sm space-y-1">
                <li>Trail map or expedition route</li>
                <li>Elevation gain graphics</li>
                <li>Basecamp to summit progression</li>
              </ul>
            </div>
          </div>
        </div>
      </ProductDesignSection>

      {/* Expedition & Wayfinding Metaphors */}
      <ProductDesignSection title="Expedition & Wayfinding Metaphors">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Our metaphors aren't superficial—they're structural and conceptual, reflecting the journey from chaos to clarity, from basecamp to summit.
        </p>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-4 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-2">Layered Landscapes</h3>
            <p className="text-[var(--text-sec)] text-sm">
              Use elevation models and cartographic overlays with transparent decision layers to show depth and perspective.
            </p>
          </div>

          <div className="p-4 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-2">Clarity Beams</h3>
            <p className="text-[var(--text-sec)] text-sm">
              Visual metaphors showing how clarity cuts through fog, revealing the actual path from false choices.
            </p>
          </div>

          <div className="p-4 bg-[var(--surface-raised)] border border-[var(--rule)]">
            <h3 className="text-lg font-heading font-semibold text-[var(--text-pri)] mb-2">Catalytic Moments</h3>
            <p className="text-[var(--text-sec)] text-sm">
              Ignition points, spark trails, and flashpoints that convert stored energy into kinetic movement.
            </p>
          </div>
        </div>
      </ProductDesignSection>

      {/* Design Principles */}
      <ProductDesignSection title="Core Design Principles">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          These principles ensure every design decision reinforces Peak Priorities' unique value proposition and creates lasting impact.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-[var(--green)]" />
              Do's
            </h3>
            <ul className="list-disc list-inside text-[var(--text-sec)] space-y-2">
              <li><strong>Show the Journey:</strong> Every visual should imply movement from chaos to clarity</li>
              <li><strong>Reveal Hidden Connections:</strong> Use visual metaphors that make invisible systems visible</li>
              <li><strong>Emphasize Perspective:</strong> Altitude and vantage point should be central themes</li>
              <li><strong>Create Wayfinding:</strong> Every element should guide the viewer toward understanding</li>
              <li><strong>Show Transformation:</strong> Before/after states, catalytic moments, energy conversion</li>
              <li><strong>Use Mathematical Harmony:</strong> Golden ratio, consistent proportions, balanced compositions</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)] mb-3 flex items-center gap-2">
              <XCircle className="h-5 w-5 text-[var(--coral)]" />
              Don'ts
            </h3>
            <ul className="list-disc list-inside text-[var(--text-sec)] space-y-2">
              <li><strong>Avoid Static Presentations:</strong> Don't show flat, lifeless diagrams</li>
              <li><strong>Don't Obscure Meaning:</strong> Visuals should clarify, not complicate</li>
              <li><strong>Avoid Generic Metaphors:</strong> No clichéd "lightbulb moments" or "thinking outside the box"</li>
              <li><strong>Don't Ignore Scale:</strong> Show how individual work connects to systemic change</li>
              <li><strong>Avoid Disconnected Elements:</strong> Every piece should relate to the whole</li>
              <li><strong>Don't Break Mathematical Harmony:</strong> Maintain consistent proportions and spacing</li>
            </ul>
          </div>
        </div>
      </ProductDesignSection>

      {/* Implementation Guidelines */}
      <ProductDesignSection title="Implementation Guidelines">
        <p className="text-[var(--text-sec)] leading-relaxed mb-6">
          Practical guidance for applying these principles across all design touchpoints.
        </p>

        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)] mb-3">Visual Hierarchy</h3>
            <ul className="list-disc list-inside text-[var(--text-sec)] space-y-2">
              <li>Use golden ratio for font sizes and spacing</li>
              <li>Create clear visual paths that guide the eye</li>
              <li>Establish consistent depth and layering</li>
              <li>Use color and contrast to reinforce meaning</li>
            </ul>
          </div>

          <div>
            <h3 className="text-xl font-heading font-semibold text-[var(--text-pri)] mb-3">Metaphor Integration</h3>
            <ul className="list-disc list-inside text-[var(--text-sec)] space-y-2">
              <li>Weave expedition themes throughout all materials</li>
              <li>Use consistent iconography for wayfinding</li>
              <li>Create visual stories that show transformation</li>
              <li>Maintain thematic coherence across touchpoints</li>
            </ul>
          </div>
        </div>
      </ProductDesignSection>

      {/* Call to Action */}
      <div className="border border-[var(--rule)] bg-[var(--surface)] p-8 text-center">
        <h3 className="text-2xl font-heading font-semibold text-[var(--text-pri)] mb-3">
          Design with Purpose, Create with Impact
        </h3>
        <p className="text-[var(--text-sec)] mb-6 max-w-2xl mx-auto">
          Every design decision should reinforce Peak Priorities' unique differentiators and create visual experiences that guide people from chaos to clarity.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <a href="/elevation/logo-system" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-[var(--slate)] hover:bg-[var(--hover-tint)]">
            <Compass className="h-5 w-5 mr-2" />
            Explore Logo System
          </a>
          <a href="/elevation/layout-grid-system" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-sage-600 hover:bg-sage-700">
            <Grid className="h-5 w-5 mr-2" />
            View Layout System
          </a>
          <a href="/elevation/lab/brand-guidelines-document" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium text-white bg-[var(--mustard)] hover:bg-[var(--mustard)]">
            <BookOpen className="h-5 w-5 mr-2" />
            Read Guidelines
          </a>
        </div>
      </div>
    </div>
  );
};
